
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Activity,
  Server,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Zap
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';

interface SystemMetric {
  name: string;
  value: number;
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  threshold?: {
    warning: number;
    critical: number;
  };
  trend?: 'up' | 'down' | 'stable';
  change?: number;
}

interface SystemService {
  name: string;
  status: 'online' | 'degraded' | 'offline';
  uptime: string;
  responseTime?: number;
  lastChecked: string;
  description?: string;
}

interface SystemAlert {
  id: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  timestamp: string;
  resolved?: boolean;
}


function getStatusColor(status: string) {
  switch (status) {
    case 'healthy':
    case 'online':
      return 'text-green-600';
    case 'warning':
    case 'degraded':
      return 'text-yellow-600';
    case 'critical':
    case 'offline':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'healthy':
    case 'online':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'warning':
    case 'degraded':
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    case 'critical':
    case 'offline':
      return <XCircle className="h-4 w-4 text-red-600" />;
    default:
      return <Activity className="h-4 w-4 text-gray-600" />;
  }
}

function getTrendIcon(trend?: string) {
  switch (trend) {
    case 'up':
      return <TrendingUp className="h-3 w-3 text-red-500" />;
    case 'down':
      return <TrendingDown className="h-3 w-3 text-green-500" />;
    default:
      return null;
  }
}

export function SystemMonitor() {
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [services, setServices] = useState<SystemService[]>([]);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch system health data
  const fetchSystemHealth = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get('/api/admin/system/health');

      if (response.success && response.data) {
        const data = response.data as { metrics?: SystemMetric[]; services?: SystemService[]; alerts?: SystemAlert[] };
        setMetrics(data.metrics || []);
        setServices(data.services || []);
        setAlerts(data.alerts || []);
      } else {
        setError(response.error?.message || 'Failed to load system health');
      }
    } catch (err) {
      console.error('Error fetching system health:', err);
      setError('Failed to load system health data');
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchSystemHealth();
    setIsRefreshing(false);
  }, [fetchSystemHealth]);

  // Subscribe to real-time system health updates via Durable Objects
  const handleMessage = useCallback((message: any) => {
    if (message.type === 'system_health_update' && message.systemHealth) {
      const data = message.systemHealth as { metrics?: SystemMetric[]; services?: SystemService[]; alerts?: SystemAlert[] };
      setMetrics(data.metrics || []);
      setServices(data.services || []);
      setAlerts(data.alerts || []);
    }
  }, []);

  const { isConnected } = useDurableObjectChannel({
    channel: 'admin:system-health',
    enabled: true,
    onMessage: handleMessage,
  });

  // Initial fetch on mount
  useEffect(() => {
    fetchSystemHealth();
  }, [fetchSystemHealth]);

  const overallHealth = metrics.every(m => m.status === 'healthy') &&
                       services.every(s => s.status === 'online')
                         ? 'healthy'
                         : metrics.some(m => m.status === 'critical') ||
                           services.some(s => s.status === 'offline')
                           ? 'critical'
                           : 'warning';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">System Monitor</h1>
          <p className="text-muted-foreground">
            Real-time system health and performance metrics
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {getStatusIcon(overallHealth)}
            <span className={`font-medium capitalize ${getStatusColor(overallHealth)}`}>
              {overallHealth}
            </span>
          </div>

          <Button onClick={handleRefresh} disabled={isRefreshing} size="icon" variant="outline">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* System Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {metrics.map((metric, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium truncate flex-1 min-w-0">{metric.name}</CardTitle>
              <div className="flex items-center gap-1 flex-shrink-0">
                {getTrendIcon(metric.trend)}
                {getStatusIcon(metric.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metric.value}
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  {metric.unit}
                </span>
              </div>

              {metric.threshold && (
                <Progress
                  value={(metric.value / metric.threshold.critical) * 100}
                  className="mt-2"
                />
              )}

              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span>
                  {metric.change !== undefined && (
                    <span className={metric.change > 0 ? 'text-red-600' : 'text-green-600'}>
                      {metric.change > 0 ? '+' : ''}{metric.change.toFixed(1)}{metric.unit}
                    </span>
                  )}
                </span>
                {metric.threshold && (
                  <span>
                    Warning: {metric.threshold.warning}{metric.unit}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Services Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 truncate">
            <Server className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">Service Status</span>
          </CardTitle>
          <CardDescription className="truncate">
            Health status of all system services and dependencies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {services.map((service, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getStatusIcon(service.status)}
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium truncate">{service.name}</h4>
                    {service.description && (
                      <p className="text-sm text-muted-foreground truncate">{service.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-6 text-sm flex-wrap">
                  <div className="text-center">
                    <div className="font-medium">{service.uptime}</div>
                    <div className="text-muted-foreground">Uptime</div>
                  </div>

                  {service.responseTime && (
                    <div className="text-center">
                      <div className="font-medium">{service.responseTime}ms</div>
                      <div className="text-muted-foreground">Response</div>
                    </div>
                  )}

                  <Badge
                    variant={
                      service.status === 'online' ? 'default' :
                      service.status === 'degraded' ? 'secondary' : 'destructive'
                    }
                    className="capitalize"
                  >
                    {service.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 truncate">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">System Alerts</span>
          </CardTitle>
          <CardDescription className="truncate">
            Recent system events and notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>All systems operational</p>
                <p className="text-sm">No active alerts</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <Alert key={alert.id} className={
                  alert.level === 'critical' ? 'border-red-500' :
                  alert.level === 'error' ? 'border-red-400' :
                  alert.level === 'warning' ? 'border-yellow-500' :
                  'border-blue-500'
                }>
                  <div className="flex items-start gap-3">
                    {getStatusIcon(alert.level === 'critical' || alert.level === 'error' ? 'critical' : 'warning')}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{alert.title}</h4>
                        <Badge variant={
                          alert.level === 'critical' ? 'destructive' :
                          alert.level === 'error' ? 'destructive' :
                          alert.level === 'warning' ? 'secondary' : 'default'
                        } className="text-xs">
                          {alert.level}
                        </Badge>
                        {alert.resolved && (
                          <Badge variant="outline" className="text-xs">
                            Resolved
                          </Badge>
                        )}
                      </div>
                      <AlertDescription>{alert.message}</AlertDescription>
                      <div className="text-xs text-muted-foreground mt-2">
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Alert>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Charts Placeholder */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Response Time Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-center justify-center bg-muted/20 rounded">
              <div className="text-center">
                <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Performance chart would go here</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Avg: 245ms | Peak: 1200ms | Min: 45ms
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Resource Utilization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>CPU</span>
                  <span>45%</span>
                </div>
                <Progress value={45} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Memory</span>
                  <span>68%</span>
                </div>
                <Progress value={68} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Storage</span>
                  <span>24%</span>
                </div>
                <Progress value={24} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Network</span>
                  <span>15%</span>
                </div>
                <Progress value={15} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
